// React and Context
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarsinetContext } from '../../../../../context/marsinetProvider';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';

// PrimeReact Components
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import {
  TestProps,
} from 'apps/marsinet/src/app/models/ingestionModels';

// Utilities
import { configureAxios } from 'apps/marsinet/src/app/utils/utils';
import {
  formatUtcTimestamp,
  getUtcDayRange,
} from 'apps/marsinet/src/app/utils/timezone-utils';

// Custom Components
import Graphs from '../graphs/graphs';
import { ProgressBarComponent } from 'apps/marsinet/src/app/components/progress-bar/progress-bar';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import { splitZipName, ROLES, getDeviceLabel } from '@marsinet/commons';
import {
  IngestionTableDto,
  FindAllIngestionsQueryDtoSortFieldEnum as SortFieldEnum,
  FindAllIngestionsQueryDtoSortOrderEnum as SortOrderEnum,
} from 'apps/marsinet/src/app/typescript-axios';

/**
 * PrimeReact hands back `sortField` as a plain string; the generated client
 * expects the enum. Narrowing instead of casting means an unknown column
 * simply doesn't sort, rather than sending a field the API rejects.
 */
const SORT_FIELDS: string[] = Object.values(SortFieldEnum);
const isSortField = (value: unknown): value is SortFieldEnum =>
  typeof value === 'string' && SORT_FIELDS.includes(value);

type FilterValue = string | number | boolean | Date | null;
type FilterMeta = { value: FilterValue; matchMode: FilterMatchMode };
type IngestionFilters = Record<string, FilterMeta>;

// p_name and created_at are gone from the API's search; ingestion_date
// replaces the latter and filters on the date encoded in the zip name.
const EMPTY_FILTERS: IngestionFilters = {
  zip_data: { value: null, matchMode: FilterMatchMode.CONTAINS },
  d_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  process: { value: null, matchMode: FilterMatchMode.EQUALS },
  role: { value: null, matchMode: FilterMatchMode.EQUALS },
  ingestion_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
};

/**
 * PrimeReact stores filters as `{ field: { value, matchMode } }`; the API's
 * `search` object wants a flat `{ field: value }`. The backend applies
 * `contains` to every text filter and ignores matchMode, so it is dropped.
 */
const toSearchPayload = (filters: IngestionFilters, timeZone: string) =>
  Object.entries(filters).reduce<Record<string, string | number | boolean>>(
    (acc, [key, meta]) => {
      const value = meta?.value;
      if (value !== null && value !== undefined && value !== '') {
        if (key === 'ingestion_date' && value instanceof Date) {
          // Envía el día local seleccionado como un intervalo UTC exacto.
          const { start, endExclusive } = getUtcDayRange(value, timeZone);
          acc.ingestion_date_from = start.toISOString();
          acc.ingestion_date_to = endExclusive.toISOString();
          return acc;
        }
        if (!(value instanceof Date)) {
          acc[key] = value;
        }
      }
      return acc;
    },
    {}
  );

export function Tests(props: Readonly<TestProps>) {
  const { selectedProducts, setSelectedProducts } = props;
  const { access_token, setRole, setAdmin, setError, selectedTimezone } =
    useContext(MarsinetContext);
  const { t, i18n } = useTranslation();
  const { showToast: show } = useToast();

  const [loading, setLoading] = useState<boolean>(true);
  const [ingestions, setIngestions] = useState<IngestionTableDto[]>([]);
  const [ingestionId, setIngestionId] = useState<string>('');
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [modelDevices, setModelDevices] = useState<string>('');
  const [showGraphs, setShowGraphs] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [zip_data, setZip_data] = useState<string>('');
  const [deviceNames, setDeviceNames] = useState({});
  const [filters, setFilters] = useState<IngestionFilters>(EMPTY_FILTERS);
  const [sortField, setSortField] = useState<SortFieldEnum | undefined>(
    undefined
  );
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.Desc);

  // Volver a la primera página cuando cambian filtros u orden,
  // si no la petición pide un offset que ya no existe.
  useEffect(() => {
    setPage(0);
  }, [filters, sortField, sortOrder]);

  useEffect(() => {
    if (!access_token) return;
    configureAxios(access_token);
    loadIngestions(page + 1, pageSize, filters, sortField, sortOrder);
    apiServices.myAccountApi.myAccountControllerGetData().then((response) => {
      const data = response.data;
      setRole(data.organization?.role);
      setAdmin(data.can_edit);
    });
  }, [access_token, page, pageSize, filters, sortField, sortOrder]);

  useEffect(() => {
    if (!access_token) return;
    configureAxios(access_token);
    apiServices.devicesApi.devicesControllerFindAll().then((response) => {
      const data = response.data;
      const devices = {};
      data.forEach((device) => {
        devices[device.d_id] = {
          serial: device.serial,
          model: device.model,
        };
      });
      setDeviceNames(devices);
    });
  }, [access_token]);

  const initFilters = () => {
    setFilters(EMPTY_FILTERS);
  };

  const loadIngestions = async (
    page: number,
    pageSize: number,
    filters: IngestionFilters,
    sort_field: SortFieldEnum = SortFieldEnum.IngestionDate,
    sort_order: SortOrderEnum = SortOrderEnum.Desc
  ) => {
    setLoading(true);
    try {
      // POST with a body now. is_test used to be a positional argument of its
      // own; it lives inside `search` alongside the column filters.
      const response =
        await apiServices.ingestionApi.ingestionControllerFindAll({
          page,
          limit: pageSize,
          search: { ...toSearchPayload(filters, selectedTimezone), is_test: true },
          sort_field,
          sort_order,
        });
      if (response.data) {
        setIngestions(response.data.data);
        setTotalRecords(response.data.total_records);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        setError(true);
        logEvent(analytics, 'error_marsinet', {
          error_message: error.message,
          error_code: error.response?.status,
          error_page: window.location.pathname,
          error_stack: error.stack || 'No stack trace',
          timestamp: new Date().toISOString(),
          browser_info: navigator.userAgent,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDevice = (d_id: string, property: string) => {
    const device = deviceNames[d_id];
    return device ? device[property] : 'N/A';
  };

  const buttonGraphs = (row) => {
    return (
      <div className="d-flex justify-content-center">
        <Button
          className="p-button-rounded p-button-secondary p-button-text p-button-lg"
          icon="pi pi-chart-bar"
          onClick={() => {
            setShowGraphs(true);
            setIngestionId(row.id);
            setZip_data(row.zip_data);
            setSerialNumber(formatDevice(row.d_id, 'serial'));
            setModelDevices(formatDevice(row.d_id, 'model'));
          }}
          tooltip={t('pages.ingestions.graphs')}
          tooltipOptions={{
            position: 'bottom',
            mouseTrack: true,
            mouseTrackTop: 15,
          }}
        />
      </div>
    );
  };

  const button = (row) => {
    return (
      <div className="d-flex justify-content-center">
        <Button
          className="p-button-rounded p-button-secondary p-button-text p-button-lg"
          icon="pi pi-download"
          onClick={() => {
            downloadData(row.zip_data);
          }}
          tooltip={t('pages.ingestions.download')}
          tooltipOptions={{
            position: 'bottom',
            mouseTrack: true,
            mouseTrackTop: 15,
          }}
        />
      </div>
    );
  };

  const downloadData = async (zip_data: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any =
        await apiServices.ingestionApi.ingestionControllerDownloadFile(
          zip_data,
          { responseType: 'blob' }
        );
      if (response.data) {
        const blob = new Blob([response.data], { type: 'application/zip' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = splitZipName(zip_data);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        show(
          'success',
          t('messages.success'),
          t('messages.ingestion.success_donwload')
        );
      } else {
        show('error', t('messages.error'), t('messages.ingestion.empty_file'));
      }
    } catch (_error) {
      show(
        'error',
        t('messages.error'),
        t('messages.ingestion.error_downloading')
      );
    }
  };

  // Filtro por rol de la organización del usuario que creó la ingesta
  const roleFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      options={Object.values(ROLES).map((r) => ({
        label: t(`roles.${r}`),
        value: r,
      }))}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder={t('pages.ingestions.selectRole')}
      className="p-column-filter"
      showClear
    />
  );

  // Local YYYY-MM-DD — toISOString() would shift to UTC and return
  // the previous day for users behind UTC.
  const toISODate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;

  const formatIngestionDate = (value?: string | Date | null) => {
    if (!value) return 'N/A';
    // Las fechas de prueba llegan en UTC; solo se adapta su presentación.
    return formatUtcTimestamp(value, selectedTimezone, 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const dateFilterTemplate = (options) => (
    <Calendar
      value={options.value ? new Date(`${options.value}T00:00:00`) : null}
      onChange={(e) =>
        options.filterApplyCallback(e.value ? toISODate(e.value as Date) : null)
      }
      inline
    />
  );

  const clearFilter = () => {
    initFilters();
  };

  const renderHeader = () => {
    return (
      <div className="w-full sm:w-auto flex justify-center sm:justify-start">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          onClick={clearFilter}
          className="w-full sm:w-auto text-xs sm:text-sm px-3 py-2 lg:px-4 lg:py-2"
        />
        <div className="p-inputgroup"></div>
      </div>
    );
  };

  // PrimeReact: 1 = ascendente, -1 = descendente

  return (
    <div className="col-12 xl:pl-3 xl:pr-3">
      {showGraphs ? (
        <Graphs
          ingestionId={ingestionId}
          setShowGraphs={setShowGraphs}
          zip_data={zip_data}
          isTest={true}
          serialNumber={serialNumber}
          modelDevices={modelDevices}
        />
      ) : (
        <div className="">
          {loading ? (
            <ProgressBarComponent />
          ) : (
            <DataTable
              value={ingestions}
              paginator
              lazy
              first={page * pageSize}
              rows={pageSize}
              emptyMessage={t('emptyValuesTable')}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              selectionMode="checkbox"
              totalRecords={totalRecords}
              selection={selectedProducts}
              sortField={sortField}
              sortOrder={
                sortField
                  ? sortOrder === SortOrderEnum.Asc
                    ? 1
                    : -1
                  : undefined
              }
              removableSort
              onSelectionChange={(e) => setSelectedProducts(e.value)}
              onPage={(e) => {
                setPage(e.page);
                setPageSize(e.rows);
              }}
              onSort={(e) => {
                // Third click with `removableSort` clears the sort entirely.
                if (!e.sortField || !e.sortOrder) {
                  setSortField(undefined);
                  setSortOrder(SortOrderEnum.Desc);
                  return;
                }
                if (!isSortField(e.sortField)) return;
                setSortField(e.sortField);
                setSortOrder(
                  e.sortOrder === 1 ? SortOrderEnum.Asc : SortOrderEnum.Desc
                );
              }}
              loading={loading}
              dataKey="id"
              filters={filters}
              globalFilterFields={[
                'zip_data',
                'd_name',
                'process',
                'role',
                'ingestion_date',
              ]}
              header={renderHeader()}
              onFilter={(e) => {
                setPage(0);
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  ...(e.filters as IngestionFilters),
                }));
              }}
            >
              <Column
                selectionMode="multiple"
                headerStyle={{ width: '3rem' }}
              ></Column>

              <Column
                field="d_name"
                header={t('pages.maintenance.ingestion.d_name')}
                style={{ width: '15rem' }}
                filter
                showFilterMatchModes={false}
                filterPlaceholder={t('pages.ingestions.search_d_name')}
                body={(rowData) => (
                  <div className="pl-3">
                    {formatDevice(rowData.d_id, 'serial')}
                  </div>
                )}
              />
              <Column
                header={t('pages.ingestions.model')}
                style={{ width: '12rem' }}
                body={(rowData) => (
                  <div className="pl-3">
                    {getDeviceLabel(formatDevice(rowData.d_id, 'model')) ??
                      'N/A'}
                  </div>
                )}
              />

              <Column
                field="u_role"
                filterField="role"
                header={t('pages.ingestions.role')}
                style={{ width: '12rem' }}
                filter
                showFilterMatchModes={false}
                showApplyButton={false}
                filterElement={roleFilterTemplate}
                body={(rowData) => (
                  <div className="pl-3">
                    {rowData.u_role ? t(`roles.${rowData.u_role}`) : 'N/A'}
                  </div>
                )}
              />

              <Column
                field="date"
                sortField="ingestion_date"
                filterField="ingestion_date"
                header={t('pages.ingestions.date')}
                style={{ width: '15rem' }}
                filter
                sortable
                showFilterMatchModes={false}
                showApplyButton={false}
                filterElement={dateFilterTemplate}
                body={(rowData) => (
                  <div className="pl-3">
                    {formatIngestionDate(rowData.date)}
                  </div>
                )}
              />

              <Column
                header={t('pages.ingestions.graphs')}
                body={buttonGraphs}
                style={{ width: '5rem' }}
              />

              <Column
                header={t('pages.ingestions.download')}
                body={button}
                style={{ width: '5rem' }}
              />
            </DataTable>
          )}
        </div>
      )}
    </div>
  );
}

export default Tests;